import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Tag,
  Mail,
  Upload,
  ExternalLink,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, end: true },
  { name: 'Ürünler', href: '/admin/products', icon: Package },
  { name: 'Excel Yükle', href: '/admin/excel-upload', icon: Upload },
  { name: 'Siparişler', href: '/admin/orders', icon: ShoppingBag },
  { name: 'Değerlendirmeler', href: '/admin/reviews', icon: Star },
  { name: 'Kullanıcılar', href: '/admin/users', icon: Users },
  { name: 'Kategoriler', href: '/admin/categories', icon: Tag },
  { name: 'Newsletter', href: '/admin/newsletter', icon: Mail },
  { name: 'Ayarlar', href: '/admin/settings', icon: Settings }
];

const SidebarLink = ({ item, onNavigate }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
          isActive
            ? 'bg-orange-500/15 text-orange-300 shadow-sm ring-1 ring-orange-500/30'
            : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`h-5 w-5 shrink-0 ${
              isActive ? 'text-orange-400' : 'text-slate-400 group-hover:text-slate-200'
            }`}
          />
          {item.name}
        </>
      )}
    </NavLink>
  );
};

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();

  const closeMobile = () => setSidebarOpen(false);

  const sidebarContent = (mobile = false) => (
    <>
      <div className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-700/80 px-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-sm font-bold text-white shadow-lg shadow-orange-900/40">
          LT
        </div>
        <div>
          <p className="text-sm font-semibold text-white">LeaserTech</p>
          <p className="text-xs text-slate-400">Yönetim paneli</p>
        </div>
        {mobile && (
          <button
            type="button"
            onClick={closeMobile}
            className="ml-auto text-slate-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {navigation.map((item) => (
          <SidebarLink
            key={item.href}
            item={item}
            onNavigate={mobile ? closeMobile : undefined}
          />
        ))}
      </nav>

      <div className="shrink-0 border-t border-slate-700/80 p-4">
        <div className="flex items-center gap-3 rounded-xl bg-slate-800/60 p-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-700">
            <User className="h-5 w-5 text-slate-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="text-xs text-slate-400">Admin</p>
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-300 transition hover:border-slate-500 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {sidebarOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            aria-label="Menüyü kapat"
            onClick={closeMobile}
          />
          <aside className="relative z-10 flex h-full w-72 max-w-[85vw] flex-col bg-slate-900 shadow-2xl">
            {sidebarContent(true)}
          </aside>
        </div>
      )}

      <div className="lg:grid lg:min-h-screen lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="sticky top-0 hidden h-screen flex-col bg-gradient-to-b from-slate-900 to-slate-950 shadow-xl lg:flex">
          {sidebarContent(false)}
        </aside>

        <div className="flex min-w-0 flex-col">
          <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
            <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
                aria-label="Menüyü aç"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="hidden flex-1 lg:block" />
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-orange-200 hover:text-orange-600"
              >
                <ExternalLink className="h-4 w-4" />
                Siteye Git
              </Link>
            </div>
          </header>

          <main className="min-h-[calc(100vh-3.5rem)] flex-1 p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
